import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Shuffle, RotateCcw, ArrowRight, Quote } from "lucide-react";

function Drill({ obstacle, structure, onBack, onRandom }) {
  const [revealed, setRevealed] = useState(0);
  const [showCoach, setShowCoach] = useState(false);
  useEffect(() => { setRevealed(0); setShowCoach(false); }, [obstacle]);
  const done = revealed >= obstacle.steps.length;

  return (
    <div className="max-w-3xl space-y-6" data-testid="obstacle-drill">
      <button onClick={onBack} className="label-eyebrow" data-testid="drill-back">← All obstacles</button>

      <div className="border-2 border-[color:var(--brand-primary)] bg-[color:var(--brand-primary)] text-white p-8">
        <div className="flex items-center justify-between">
          <Quote size={18} strokeWidth={1.25} />
          <span className="label-eyebrow text-white/60">Obstacle {obstacle.num} · {obstacle.category}</span>
        </div>
        <p className="mt-4 font-display text-2xl md:text-3xl leading-snug" data-testid="drill-quote">&ldquo;{obstacle.says}&rdquo;</p>
        <p className="mt-3 text-sm text-white/60">Say your response out loud before revealing each step.</p>
      </div>

      <div className="space-y-3">
        {obstacle.steps.map((s, i) => (
          <div key={i} className={`border hairline bg-white transition-opacity ${i < revealed ? "" : "opacity-40"}`}>
            <div className="px-5 py-3 flex items-center gap-3 border-b hairline">
              <span className="w-6 h-6 flex items-center justify-center text-[11px] font-mono bg-[color:var(--brand-primary)] text-white">{i + 1}</span>
              <span className="label-eyebrow">{s.label}</span>
            </div>
            {i < revealed ? (
              <p className="px-5 py-4 text-sm leading-relaxed" data-testid={`drill-step-${i + 1}`}>{s.text}</p>
            ) : (
              <p className="px-5 py-4 text-sm text-neutral-400 italic select-none">Hidden — reveal when you've said yours.</p>
            )}
          </div>
        ))}
      </div>

      {!done ? (
        <button onClick={() => setRevealed(revealed + 1)} className="btn-primary" data-testid="drill-reveal-btn">
          Reveal step {revealed + 1} — {obstacle.steps[revealed]?.label}
        </button>
      ) : !showCoach ? (
        <button onClick={() => setShowCoach(true)} className="btn-primary" data-testid="drill-coach-btn">Show coach note</button>
      ) : (
        <div className="border-l-2 border-[#a05a00] bg-[#a05a00]/5 px-5 py-4" data-testid="drill-coach-note">
          <div className="label-eyebrow text-[#a05a00] mb-1">Coach note</div>
          <p className="text-sm leading-relaxed">{obstacle.coach_note}</p>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button onClick={() => { setRevealed(0); setShowCoach(false); }} className="btn-ghost text-sm flex items-center gap-2" data-testid="drill-restart-btn">
          <RotateCcw size={14} /> Restart this one
        </button>
        <button onClick={onRandom} className="btn-ghost text-sm flex items-center gap-2" data-testid="drill-random-btn">
          <Shuffle size={14} /> Random obstacle
        </button>
      </div>
    </div>
  );
}

export default function ObstacleTrainer() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/obstacle-trainer").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-sm text-neutral-500">Loading obstacles…</div>;

  const random = () => {
    const others = data.obstacles.filter((o) => o.num !== active?.num);
    setActive(others[Math.floor(Math.random() * others.length)]);
  };

  if (active) return <Drill obstacle={active} structure={data.structure} onBack={() => setActive(null)} onRandom={random} />;

  return (
    <div className="space-y-10" data-testid="obstacle-trainer">
      <div>
        <Link to="/scenarios" className="label-eyebrow" data-testid="trainer-back">← Role-Play Studio</Link>
        <div className="label-eyebrow mb-3 mt-4 text-[var(--brand-primary)]">Obstacle Trainer</div>
        <h1 className="font-display text-5xl md:text-6xl leading-[1.02]">{data.obstacles.length} obstacles. <em className="italic">One structure.</em></h1>
        <p className="mt-4 text-neutral-500 max-w-2xl">Pick an obstacle, say your response out loud, then reveal the structured answer one step at a time. Absorb the structure — not the words.</p>
      </div>

      <div className="border hairline bg-[color:var(--brand-surface2)] p-5 flex flex-wrap gap-x-8 gap-y-2" data-testid="five-step-bar">
        {data.structure.map((s) => (
          <div key={s.num} className="flex items-center gap-2 text-xs">
            <span className="w-5 h-5 flex items-center justify-center text-[10px] font-mono border border-[color:var(--brand-primary)] text-[var(--brand-primary)]">{s.num}</span>
            <span className="font-medium">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div className="label-eyebrow">Choose your obstacle</div>
        <button onClick={random} className="btn-ghost text-sm flex items-center gap-2" data-testid="trainer-random-btn">
          <Shuffle size={14} /> Drill me at random
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.obstacles.map((o) => (
          <button key={o.num} onClick={() => setActive(o)} data-testid={`obstacle-card-${o.num}`}
                  className="card-hairline p-6 text-left flex flex-col gap-3 group hover:border-[color:var(--brand-primary)] transition-colors">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-neutral-400">{o.num}</span>
              <span className="label-eyebrow">{o.category}</span>
            </div>
            <p className="font-display text-lg leading-snug">&ldquo;{o.says}&rdquo;</p>
            <span className="mt-auto inline-flex items-center gap-1 text-sm link-underline">
              Drill it <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
